import React from "react";
import { motion } from "framer-motion";
import { QrCode, HandHeart, Fingerprint } from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: "easeOut" },
};

const Overline = ({ children }) => (
  <span className="text-xs uppercase tracking-[0.22em] font-semibold text-qh-rose">
    {children}
  </span>
);

export default function QHeartProduct() {
  return (
    <section
      id="qheart"
      className="relative py-24 sm:py-32 px-6 sm:px-8 lg:px-12 overflow-hidden bg-white"
      data-testid="qheart-product"
    >
      {/* Soft background accents */}
      <div className="pointer-events-none absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-[#FFE4E6] opacity-60 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 w-[360px] h-[360px] rounded-full bg-[#FFE4E6] opacity-50 blur-3xl" />

      <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        {/* Left — copy (left-aligned like other sections) */}
        <motion.div {...fadeUp}>
          <Overline>Das QHeart</Overline>
          <h2 className="mt-5 font-display font-medium text-4xl sm:text-5xl lg:text-6xl tracking-tight text-qh-ink leading-[1.05]">
            Kein digitales Like.<br />
            <span className="italic text-qh-rose">Ein echtes Zeichen.</span>
          </h2>
          <p className="mt-6 text-lg text-qh-slate leading-relaxed max-w-xl">
            Jedes QHeart ist ein physisches Objekt — in der App gewählt,
            personalisiert mit deinem Profil und deinen Worten, in der Hand
            übergeben. Auf der Rückseite: dein persönlicher QR-Code. Nur wer ihn
            scannt, sieht, was du sagen wolltest.
          </p>

          <ul className="mt-10 space-y-5" data-testid="qheart-features">
            {[
              {
                icon: Fingerprint,
                title: "Individuell.",
                body: "Jedes QHeart führt ausschließlich zu deinem Profil — und zu deiner Nachricht für genau diesen Menschen.",
              },
              {
                icon: HandHeart,
                title: "Real übergeben.",
                body: "Kein Screenshot. Kein Link. Ein Moment zwischen zwei echten Menschen — den man spürt, nicht scrollt.",
              },
              {
                icon: QrCode,
                title: "Still und ehrlich.",
                body: "Gescannt wird, wenn die Person bereit ist. Kein Druck. Kein Matching-Spiel. Nur eine Entscheidung, die ganz in eigener Hand liegt.",
              },
            ].map((f) => (
              <li key={f.title} className="flex gap-4">
                <span className="shrink-0 h-11 w-11 rounded-full border border-qh-line flex items-center justify-center bg-white">
                  <f.icon className="w-5 h-5 text-qh-rose" strokeWidth={1.5} />
                </span>
                <div>
                  <h4 className="font-display text-xl font-medium text-qh-ink">
                    {f.title}
                  </h4>
                  <p className="mt-1 text-qh-slate leading-relaxed">{f.body}</p>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-10 inline-flex items-center gap-2 rounded-full border border-qh-rose/30 bg-[#FFE4E6]/60 px-5 py-2 text-sm font-semibold text-qh-rose tracking-wide">
            Keep Dating Real.
          </div>
        </motion.div>

        {/* Right — visual */}
        <motion.div
          {...fadeUp}
          className="relative"
          data-testid="qheart-visual"
        >
          <div className="relative mx-auto max-w-lg aspect-[5/4]">
            {/* Back — subtle, behind */}
            <motion.img
              src="/assets/qheart-back.png"
              alt="QHeart Rückseite mit QR-Code"
              draggable="false"
              initial={{ opacity: 0, x: 40, rotate: 12 }}
              whileInView={{ opacity: 1, x: 0, rotate: 8 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
              className="absolute top-[6%] right-[4%] w-[62%] select-none"
              style={{
                filter: "drop-shadow(0 30px 40px rgba(10,10,10,0.15))",
              }}
            />
            {/* Front — in front */}
            <motion.img
              src="/assets/qheart-front.png"
              alt="QHeart Vorderseite"
              draggable="false"
              initial={{ opacity: 0, x: -40, rotate: -12 }}
              whileInView={{ opacity: 1, x: 0, rotate: -6 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: "easeOut", delay: 0.05 }}
              className="absolute top-[2%] left-[2%] w-[66%] select-none"
              style={{
                filter: "drop-shadow(0 40px 50px rgba(225,29,72,0.28))",
              }}
            />

            {/* Floating labels */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.7 }}
              className="absolute left-0 bottom-0 rounded-full border border-qh-line bg-white px-4 py-2 text-xs font-medium text-qh-ink shadow-[0_20px_30px_-20px_rgba(10,10,10,0.2)]"
            >
              Vorderseite · Zeichen
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.9 }}
              className="absolute right-0 top-4 rounded-full border border-qh-line bg-white px-4 py-2 text-xs font-medium text-qh-ink shadow-[0_20px_30px_-20px_rgba(10,10,10,0.2)]"
            >
              Rückseite · Dein QR
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
